import React, { useState, useEffect } from 'react';
import axios from 'axios';
import toast from 'react-hot-toast';
import { useAuthStore } from '../stores/authStore';
import { usePermissions } from '../hooks/usePermissions';
import {
  CircleStackIcon,
  BeakerIcon,
  CheckCircleIcon,
  XCircleIcon,
  ExclamationTriangleIcon,
  InformationCircleIcon,
} from '@heroicons/react/24/outline';

type TabType = 'migrations' | 'seeds';
type Environment = 'dev' | 'prod';

interface SeedDefinition {
  id: string;
  name: string;
  description: string;
  category: string;
  environment: 'dev' | 'prod' | 'both';
  dangerous?: boolean;
  requiresAdmin?: boolean;
  dependencies?: string[];
  feature?: string;
}

interface MigrationInfo {
  name: string;
  applied: boolean;
  appliedAt?: string;
  description?: string;
}

interface MigrationStatus {
  pending: MigrationInfo[];
  applied: MigrationInfo[];
  total: number;
  needsBaseline?: boolean;
}

const AdminWizard: React.FC = () => {
  const { token, user } = useAuthStore();
  const { hasPermission, hasModuleAdmin, loading: permissionsLoading } = usePermissions() || { 
    hasPermission: () => false, 
    hasModuleAdmin: () => false,
    loading: true 
  };
  const [activeTab, setActiveTab] = useState<TabType>('migrations');
  const [environment, setEnvironment] = useState<Environment>('dev');
  const [loading, setLoading] = useState(true);

  // Migration state
  const [migrationStatus, setMigrationStatus] = useState<MigrationStatus | null>(null);
  const [runningMigrations, setRunningMigrations] = useState<Set<string>>(new Set());
  const [deployingMigrations, setDeployingMigrations] = useState(false);
  const [deployOutput, setDeployOutput] = useState<string | null>(null);

  // Seed state
  const [seeds, setSeeds] = useState<SeedDefinition[]>([]);
  const [seedsByFeature, setSeedsByFeature] = useState<Record<string, SeedDefinition[]>>({});
  const [selectedSeeds, setSelectedSeeds] = useState<Set<string>>(new Set());
  const [runningSeeds, setRunningSeeds] = useState(false);
  const [seedValidation, setSeedValidation] = useState<{
    valid: boolean;
    errors: string[];
    suggestedOrder: string[];
  } | null>(null);

  useEffect(() => {
    if (activeTab === 'migrations') {
      fetchMigrationStatus();
    } else {
      fetchSeeds();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [activeTab, environment]);

  const fetchMigrationStatus = async () => {
    try {
      setLoading(true);
      const response = await axios.get('/api/admin/migrations/status', {
        headers: { Authorization: `Bearer ${token}` },
      });
      setMigrationStatus(response.data.data);
    } catch (error: any) {
      console.error('Error fetching migration status:', error);
      toast.error('Failed to load migration status');
    } finally {
      setLoading(false);
    }
  };

  const fetchSeeds = async () => {
    try {
      setLoading(true);
      const [seedsResponse, groupedResponse] = await Promise.all([
        axios.get(`/api/admin/seeds?environment=${environment}`, {
          headers: { Authorization: `Bearer ${token}` },
        }),
        axios.get(`/api/admin/seeds/by-feature?environment=${environment}`, {
          headers: { Authorization: `Bearer ${token}` },
        }),
      ]);
      setSeeds(seedsResponse.data.data);
      setSeedsByFeature(groupedResponse.data.data);
      setSelectedSeeds(new Set());
      setSeedValidation(null);
    } catch (error: any) {
      console.error('Error fetching seeds:', error);
      toast.error('Failed to load seed operations');
    } finally {
      setLoading(false);
    }
  };

  const runMigration = async (migrationName: string) => {
    if (!confirm(`Are you sure you want to run migration "${migrationName}"?`)) {
      return;
    }

    setRunningMigrations(prev => new Set(prev).add(migrationName));

    try {
      const response = await axios.post(
        `/api/admin/migrations/${migrationName}/run`,
        {},
        { headers: { Authorization: `Bearer ${token}` } }
      );

      if (response.data.success) {
        toast.success(response.data.message || 'Migration completed successfully');
        await fetchMigrationStatus();
      } else {
        toast.error(response.data.error || 'Migration failed');
      }
    } catch (error: any) {
      console.error('Error running migration:', error);
      toast.error(error.response?.data?.error || 'Failed to run migration');
    } finally {
      setRunningMigrations(prev => {
        const next = new Set(prev);
        next.delete(migrationName);
        return next;
      });
    }
  };

  const runMigrateDeploy = async () => {
    const pendingCount = migrationStatus?.pending.length || 0;
    const confirmMessage = migrationStatus?.needsBaseline
      ? 'This database was set up without migration history (common after db push). Baselining will mark existing schema migrations as applied, then deploy data migrations. Continue?'
      : pendingCount > 0
        ? `Baseline schema migrations (if needed) and deploy ${pendingCount} pending migration${pendingCount === 1 ? '' : 's'}?`
        : 'Run baseline and prisma migrate deploy? This applies any pending data migrations not yet recorded in the database.';

    if (!confirm(confirmMessage)) {
      return;
    }

    setDeployingMigrations(true);
    setDeployOutput(null);

    try {
      const response = await axios.post(
        '/api/admin/migrations/deploy',
        {},
        { headers: { Authorization: `Bearer ${token}` } }
      );

      if (response.data.success) {
        toast.success(response.data.message || 'Migrations deployed successfully');
        if (response.data.output) {
          setDeployOutput(response.data.output);
        }
        await fetchMigrationStatus();
      } else {
        toast.error(response.data.error || 'Migration deploy failed');
        if (response.data.output) {
          setDeployOutput(response.data.output);
        }
      }
    } catch (error: any) {
      console.error('Error running prisma migrate deploy:', error);
      const output = error.response?.data?.output;
      if (output) {
        setDeployOutput(output);
      }
      toast.error(error.response?.data?.error || 'Failed to run prisma migrate deploy');
    } finally {
      setDeployingMigrations(false);
    }
  };

  const runAllPendingMigrations = async () => {
    await runMigrateDeploy();
  };

  const toggleSeedSelection = (seedId: string) => {
    setSelectedSeeds(prev => {
      const next = new Set(prev);
      if (next.has(seedId)) {
        next.delete(seedId);
      } else {
        next.add(seedId);
      }
      return next;
    });
    setSeedValidation(null);
  };

  const validateSeedSelection = async () => {
    if (selectedSeeds.size === 0) {
      setSeedValidation({ valid: false, errors: ['Please select at least one seed'], suggestedOrder: [] });
      return;
    }

    try {
      const response = await axios.post(
        '/api/admin/seeds/validate-order',
        { seedIds: Array.from(selectedSeeds) },
        { 
          headers: { Authorization: `Bearer ${token}` },
          params: { environment }
        }
      );
      setSeedValidation(response.data.data);
    } catch (error: any) {
      console.error('Error validating seeds:', error);
      toast.error('Failed to validate seed selection');
    }
  };

  const runSelectedSeeds = async () => {
    if (selectedSeeds.size === 0) {
      toast.error('Please select at least one seed to run');
      return;
    }

    if (!seedValidation || !seedValidation.valid) {
      toast.error('Please validate seed selection first');
      return;
    }

    const dangerousSeeds = Array.from(selectedSeeds)
      .map(id => seeds.find(s => s.id === id))
      .filter(s => s?.dangerous);

    if (dangerousSeeds.length > 0) {
      const names = dangerousSeeds.map(s => s?.name).join(', ');
      if (!confirm(`Warning: The following seeds are marked as dangerous:\n\n${names}\n\nAre you sure you want to continue?`)) {
        return;
      }
    }

    setRunningSeeds(true);

    try {
      const response = await axios.post(
        '/api/admin/seeds/run-batch',
        { seedIds: seedValidation.suggestedOrder },
        { headers: { Authorization: `Bearer ${token}` } }
      );

      if (response.data.success) {
        toast.success(response.data.message || 'Seeds completed successfully');
        setSelectedSeeds(new Set());
        setSeedValidation(null);
        await fetchSeeds();
      } else {
        toast.error(response.data.error || 'Some seeds failed');
      }
    } catch (error: any) {
      console.error('Error running seeds:', error);
      toast.error(error.response?.data?.error || 'Failed to run seeds');
    } finally {
      setRunningSeeds(false);
    }
  };

  const getCategoryColor = (category: string) => {
    const colors: Record<string, string> = {
      system: 'bg-blue-100 text-blue-800',
      erp: 'bg-green-100 text-green-800',
      support: 'bg-purple-100 text-purple-800',
      knowledge_base: 'bg-yellow-100 text-yellow-800',
      sample_data: 'bg-indigo-100 text-indigo-800',
      multi_tenant: 'bg-red-100 text-red-800',
    };
    return colors[category] || 'bg-gray-100 text-gray-800';
  };

  // Wait for permissions to load
  if (permissionsLoading) {
    return (
      <div className="max-w-7xl mx-auto py-6 px-4 sm:px-6 lg:px-8">
        <div className="text-center py-12">
          <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-blue-600 mx-auto"></div>
          <p className="mt-4 text-sm text-gray-500">Loading permissions...</p>
        </div>
      </div>
    );
  }

  // Check permissions - use hasModuleAdmin if available, otherwise fall back to hasPermission
  // Also check if user has administrator role as a fallback
  const hasPermissionCheck = hasModuleAdmin ? hasModuleAdmin('users') : (hasPermission && hasPermission('users', 'admin'));
  const hasAdminRole = user?.roles?.includes('administrator') || false;
  const hasAdminAccess = hasPermissionCheck || hasAdminRole;
  
  // Debug: Log permission check (remove in production)
  useEffect(() => {
    if (!permissionsLoading) {
      console.log('AdminWizard Permission Check:', {
        hasPermission: !!hasPermission,
        hasModuleAdmin: !!hasModuleAdmin,
        hasPermissionCheck,
        hasAdminRole,
        hasAdminAccess,
        userRoles: user?.roles,
        permissionsLoading,
      });
    }
  }, [hasPermission, hasModuleAdmin, hasPermissionCheck, hasAdminRole, hasAdminAccess, user?.roles, permissionsLoading]);
  
  if (!hasAdminAccess) {
    return (
      <div className="max-w-7xl mx-auto py-6 px-4 sm:px-6 lg:px-8">
        <div className="bg-red-50 border-l-4 border-red-400 p-4">
          <div className="flex">
            <ExclamationTriangleIcon className="h-5 w-5 text-red-400" />
            <div className="ml-3">
              <p className="text-sm font-medium text-red-700">You do not have permission to access this page.</p>
              <p className="mt-2 text-xs text-red-600">
                Required permission: <code className="bg-red-100 px-1 py-0.5 rounded">users:admin</code>
              </p>
              <p className="mt-1 text-xs text-red-600">
                This page requires administrator privileges. If you believe you should have access, please contact your administrator or check your role assignments.
              </p>
              <p className="mt-2 text-xs text-gray-500">
                Check the browser console (F12) for permission debugging information.
              </p>
            </div>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="max-w-7xl mx-auto py-6 px-4 sm:px-6 lg:px-8">
      <div className="mb-6">
        <h1 className="text-3xl font-bold text-gray-900">Database Management Wizard</h1>
        <p className="mt-2 text-sm text-gray-600">
          Manage database migrations and seed operations
        </p>
      </div>

      {/* Tabs */}
      <div className="border-b border-gray-200 mb-6">
        <nav className="-mb-px flex space-x-8">
          <button
            onClick={() => setActiveTab('migrations')}
            className={`${
              activeTab === 'migrations'
                ? 'border-blue-500 text-blue-600'
                : 'border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300'
            } whitespace-nowrap py-4 px-1 border-b-2 font-medium text-sm flex items-center space-x-2`}
          >
            <CircleStackIcon className="h-5 w-5" />
            <span>Migrations</span>
            {migrationStatus && migrationStatus.pending.length > 0 && (
              <span className="ml-2 bg-red-100 text-red-800 text-xs font-medium px-2 py-0.5 rounded-full">
                {migrationStatus.pending.length} pending
              </span>
            )}
          </button>
          <button
            onClick={() => setActiveTab('seeds')}
            className={`${
              activeTab === 'seeds'
                ? 'border-blue-500 text-blue-600'
                : 'border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300'
            } whitespace-nowrap py-4 px-1 border-b-2 font-medium text-sm flex items-center space-x-2`}
          >
            <BeakerIcon className="h-5 w-5" />
            <span>Seeds</span>
          </button>
        </nav>
      </div>

      {/* Environment Selector (for seeds) */}
      {activeTab === 'seeds' && (
        <div className="mb-6 flex items-center space-x-4">
          <label className="text-sm font-medium text-gray-700">Environment:</label>
          <select
            value={environment}
            onChange={(e) => setEnvironment(e.target.value as Environment)}
            className="rounded-md border-gray-300 shadow-sm focus:border-blue-500 focus:ring-blue-500 sm:text-sm"
          >
            <option value="dev">Development</option>
            <option value="prod">Production</option>
          </select>
          <span className="text-sm text-gray-500">
            Showing seeds available for {environment === 'dev' ? 'development' : 'production'} environment
          </span>
        </div>
      )}

      {/* Migrations Tab */}
      {activeTab === 'migrations' && (
        <div className="space-y-6">
          {loading ? (
            <div className="text-center py-12">
              <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-blue-600 mx-auto"></div>
              <p className="mt-4 text-sm text-gray-500">Loading migration status...</p>
            </div>
          ) : (
            <>
              {migrationStatus?.needsBaseline && (
                <div className="bg-yellow-50 border border-yellow-200 rounded-lg p-4">
                  <div className="flex items-start">
                    <ExclamationTriangleIcon className="h-5 w-5 text-yellow-500 mt-0.5" />
                    <div className="ml-3">
                      <h3 className="text-sm font-medium text-yellow-900">Database needs baselining</h3>
                      <p className="mt-1 text-sm text-yellow-700">
                        The database has tables but no Prisma migration history (P3005). Use Baseline &amp; Deploy to
                        mark existing schema migrations as applied, then run pending data migrations.
                      </p>
                    </div>
                  </div>
                </div>
              )}

              <div className="bg-white shadow rounded-lg">
                <div className="px-6 py-4 flex items-center justify-between">
                  <div>
                    <h2 className="text-lg font-medium text-gray-900">Baseline &amp; Deploy</h2>
                    <p className="mt-1 text-sm text-gray-500">
                      Marks schema migrations as applied, then runs{' '}
                      <code className="bg-gray-100 px-1 py-0.5 rounded text-xs">npx prisma migrate deploy</code>{' '}
                      for pending data migrations.
                    </p>
                  </div>
                  <button
                    onClick={runMigrateDeploy}
                    disabled={deployingMigrations || runningMigrations.has('__all__')}
                    className="btn-primary text-sm disabled:opacity-50 disabled:cursor-not-allowed"
                  >
                    {deployingMigrations ? 'Running...' : 'Baseline & Deploy'}
                  </button>
                </div>
                {deployOutput && (
                  <div className="px-6 pb-4">
                    <pre className="text-xs bg-gray-50 border border-gray-200 rounded p-3 overflow-x-auto whitespace-pre-wrap">
                      {deployOutput}
                    </pre>
                  </div>
                )}
              </div>

              {/* Pending Migrations */}
              {migrationStatus && migrationStatus.pending.length > 0 && (
                <div className="bg-white shadow rounded-lg">
                  <div className="px-6 py-4 border-b border-gray-200 flex items-center justify-between">
                    <h2 className="text-lg font-medium text-gray-900">
                      Pending Migrations ({migrationStatus.pending.length})
                    </h2>
                    <button
                      onClick={runAllPendingMigrations}
                      disabled={runningMigrations.has('__all__')}
                      className="btn-primary text-sm disabled:opacity-50 disabled:cursor-not-allowed"
                    >
                      {runningMigrations.has('__all__') ? 'Running...' : 'Run All Pending'}
                    </button>
                  </div>
                  <div className="divide-y divide-gray-200">
                    {migrationStatus.pending.map((migration) => (
                      <div key={migration.name} className="px-6 py-4 flex items-center justify-between">
                        <div className="flex-1">
                          <h3 className="text-sm font-medium text-gray-900">{migration.name}</h3>
                          {migration.description && (
                            <p className="mt-1 text-sm text-gray-500">{migration.description}</p>
                          )}
                        </div>
                        <button
                          onClick={() => runMigration(migration.name)}
                          disabled={runningMigrations.has(migration.name)}
                          className="ml-4 btn-primary text-sm disabled:opacity-50 disabled:cursor-not-allowed"
                        >
                          {runningMigrations.has(migration.name) ? 'Running...' : 'Run'}
                        </button>
                      </div>
                    ))}
                  </div>
                </div>
              )}

              {/* Applied Migrations */}
              {migrationStatus && migrationStatus.applied.length > 0 && (
                <div className="bg-white shadow rounded-lg">
                  <div className="px-6 py-4 border-b border-gray-200">
                    <h2 className="text-lg font-medium text-gray-900">
                      Applied Migrations ({migrationStatus.applied.length})
                    </h2>
                  </div>
                  <div className="divide-y divide-gray-200">
                    {migrationStatus.applied.slice(0, 10).map((migration) => (
                      <div key={migration.name} className="px-6 py-4 flex items-center justify-between">
                        <div className="flex-1">
                          <div className="flex items-center space-x-2">
                            <CheckCircleIcon className="h-5 w-5 text-green-500" />
                            <h3 className="text-sm font-medium text-gray-900">{migration.name}</h3>
                          </div>
                          {migration.appliedAt && (
                            <p className="mt-1 text-sm text-gray-500">
                              Applied: {new Date(migration.appliedAt).toLocaleString()}
                            </p>
                          )}
                        </div>
                      </div>
                    ))}
                    {migrationStatus.applied.length > 10 && (
                      <div className="px-6 py-4 text-sm text-gray-500 text-center">
                        And {migrationStatus.applied.length - 10} more...
                      </div>
                    )}
                  </div>
                </div>
              )}

              {migrationStatus && migrationStatus.pending.length === 0 && migrationStatus.applied.length > 0 && (
                <div className="bg-green-50 border border-green-200 rounded-lg p-6">
                  <div className="flex items-start">
                    <CheckCircleIcon className="h-5 w-5 text-green-500 mt-0.5" />
                    <div className="ml-3">
                      <h3 className="text-sm font-medium text-green-900">All migrations applied</h3>
                      <p className="mt-1 text-sm text-green-700">
                        {migrationStatus.applied.length} migration{migrationStatus.applied.length === 1 ? '' : 's'} recorded in the database.
                        Use Run Migrate Deploy above if you have deployed new code with additional migrations.
                      </p>
                    </div>
                  </div>
                </div>
              )}

              {migrationStatus && migrationStatus.total === 0 && (
                <div className="bg-white shadow rounded-lg p-12 text-center">
                  <InformationCircleIcon className="h-12 w-12 text-gray-400 mx-auto" />
                  <h3 className="mt-4 text-sm font-medium text-gray-900">No migration files found</h3>
                  <p className="mt-2 text-sm text-gray-500">
                    Could not read migration files from the server. You can still use Run Migrate Deploy above.
                  </p>
                </div>
              )}

              {migrationStatus && migrationStatus.total > 0 && migrationStatus.pending.length === 0 && migrationStatus.applied.length === 0 && (
                <div className="bg-yellow-50 border border-yellow-200 rounded-lg p-6">
                  <div className="flex items-start">
                    <ExclamationTriangleIcon className="h-5 w-5 text-yellow-500 mt-0.5" />
                    <div className="ml-3">
                      <h3 className="text-sm font-medium text-yellow-900">Migrations not yet tracked</h3>
                      <p className="mt-1 text-sm text-yellow-700">
                        {migrationStatus.total} migration file{migrationStatus.total === 1 ? '' : 's'} found but none are recorded as applied.
                        Click Run Migrate Deploy to apply them.
                      </p>
                    </div>
                  </div>
                </div>
              )}
            </>
          )}
        </div>
      )}

      {/* Seeds Tab */}
      {activeTab === 'seeds' && (
        <div className="space-y-6">
          {loading ? (
            <div className="text-center py-12">
              <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-blue-600 mx-auto"></div>
              <p className="mt-4 text-sm text-gray-500">Loading seeds...</p>
            </div>
          ) : (
            <>
              {/* Seed Selection Summary */}
              {selectedSeeds.size > 0 && (
                <div className="bg-blue-50 border border-blue-200 rounded-lg p-4">
                  <div className="flex items-center justify-between">
                    <div>
                      <p className="text-sm font-medium text-blue-900">
                        {selectedSeeds.size} seed{selectedSeeds.size !== 1 ? 's' : ''} selected
                      </p>
                      <p className="mt-1 text-sm text-blue-700">
                        {Array.from(selectedSeeds).map(id => seeds.find(s => s.id === id)?.name).join(', ')}
                      </p>
                    </div>
                    <div className="flex space-x-2">
                      <button
                        onClick={validateSeedSelection}
                        className="btn-secondary text-sm"
                      >
                        Validate Order
                      </button>
                      <button
                        onClick={runSelectedSeeds}
                        disabled={!seedValidation?.valid || runningSeeds}
                        className="btn-primary text-sm disabled:opacity-50 disabled:cursor-not-allowed"
                      >
                        {runningSeeds ? 'Running...' : 'Run Selected'}
                      </button>
                    </div>
                  </div>
                  {seedValidation && (
                    <div className={`mt-4 p-3 rounded ${seedValidation.valid ? 'bg-green-50' : 'bg-red-50'}`}>
                      {seedValidation.valid ? (
                        <div className="flex items-start">
                          <CheckCircleIcon className="h-5 w-5 text-green-500 mt-0.5" />
                          <div className="ml-3">
                            <p className="text-sm font-medium text-green-800">Valid selection</p>
                            <p className="mt-1 text-sm text-green-700">
                              Suggested execution order: {seedValidation.suggestedOrder.join(' → ')}
                            </p>
                          </div>
                        </div>
                      ) : (
                        <div className="flex items-start">
                          <XCircleIcon className="h-5 w-5 text-red-500 mt-0.5" />
                          <div className="ml-3">
                            <p className="text-sm font-medium text-red-800">Validation errors:</p>
                            <ul className="mt-1 text-sm text-red-700 list-disc list-inside">
                              {seedValidation.errors.map((error, i) => (
                                <li key={i}>{error}</li>
                              ))}
                            </ul>
                          </div>
                        </div>
                      )}
                    </div>
                  )}
                </div>
              )}

              {/* Seeds by Feature */}
              {Object.entries(seedsByFeature).map(([feature, featureSeeds]) => (
                <div key={feature} className="bg-white shadow rounded-lg">
                  <div className="px-6 py-4 border-b border-gray-200">
                    <h2 className="text-lg font-medium text-gray-900 capitalize">
                      {feature === 'erp' ? 'ERP' : feature === 'knowledge_base' ? 'Knowledge Base' : feature} Seeds
                    </h2>
                  </div>
                  <div className="divide-y divide-gray-200">
                    {featureSeeds.map((seed) => (
                      <div key={seed.id} className="px-6 py-4">
                        <div className="flex items-start">
                          <input
                            type="checkbox"
                            checked={selectedSeeds.has(seed.id)}
                            onChange={() => toggleSeedSelection(seed.id)}
                            className="mt-1 h-4 w-4 text-blue-600 focus:ring-blue-500 border-gray-300 rounded"
                          />
                          <div className="ml-3 flex-1">
                            <div className="flex items-center space-x-2">
                              <h3 className="text-sm font-medium text-gray-900">{seed.name}</h3>
                              <span className={`inline-flex px-2 py-0.5 text-xs font-medium rounded ${getCategoryColor(seed.category)}`}>
                                {seed.category}
                              </span>
                              {seed.dangerous && (
                                <span className="inline-flex px-2 py-0.5 text-xs font-medium rounded bg-red-100 text-red-800">
                                  Dangerous
                                </span>
                              )}
                              {seed.environment !== 'both' && (
                                <span className="inline-flex px-2 py-0.5 text-xs font-medium rounded bg-gray-100 text-gray-800">
                                  {seed.environment === 'dev' ? 'Dev Only' : 'Prod Only'}
                                </span>
                              )}
                            </div>
                            <p className="mt-1 text-sm text-gray-500">{seed.description}</p>
                            {seed.dependencies && seed.dependencies.length > 0 && (
                              <p className="mt-2 text-xs text-gray-400">
                                Depends on: {seed.dependencies.join(', ')}
                              </p>
                            )}
                          </div>
                        </div>
                      </div>
                    ))}
                  </div>
                </div>
              ))}

              {seeds.length === 0 && (
                <div className="bg-white shadow rounded-lg p-12 text-center">
                  <InformationCircleIcon className="h-12 w-12 text-gray-400 mx-auto" />
                  <h3 className="mt-4 text-sm font-medium text-gray-900">No seeds available</h3>
                  <p className="mt-2 text-sm text-gray-500">
                    No seeds are available for the {environment} environment.
                  </p>
                </div>
              )}
            </>
          )}
        </div>
      )}
    </div>
  );
};

export default AdminWizard;
